import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { projectsApi } from '../utils/api';
import {
    Plus,
    FolderKanban,
    Search,
    MoreVertical,
    Trash2,
    ExternalLink,
    Globe
} from 'lucide-react';

export default function Projects() {
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['projects'],
        queryFn: () => projectsApi.list(),
    });

    const deleteMutation = useMutation({
        mutationFn: (id: string) => projectsApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
        },
    });

    const projects = data?.data?.projects || [];
    const filteredProjects = projects.filter((p: any) =>
        p.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold">Projects</h1>
                    <p className="text-dark-400 mt-1">Manage your marketing projects</p>
                </div>
                <button
                    onClick={() => setShowCreateModal(true)}
                    className="btn btn-primary flex items-center gap-2"
                >
                    <Plus className="w-5 h-5" />
                    New Project
                </button>
            </div>

            {/* Search */}
            <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-dark-400" />
                <input
                    type="text"
                    placeholder="Search projects..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="input pl-10"
                />
            </div>

            {/* Projects Grid */}
            {isLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="card animate-pulse">
                            <div className="h-40 bg-dark-700 rounded-lg" />
                        </div>
                    ))}
                </div>
            ) : filteredProjects.length === 0 ? (
                <div className="card text-center py-16">
                    <FolderKanban className="w-16 h-16 text-dark-600 mx-auto mb-4" />
                    <h3 className="text-xl font-medium mb-2">
                        {searchQuery ? 'No projects found' : 'No projects yet'}
                    </h3>
                    <p className="text-dark-400 mb-6">
                        {searchQuery
                            ? 'Try a different search term'
                            : 'Create your first project to start managing your marketing'}
                    </p>
                    {!searchQuery && (
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className="btn btn-primary inline-flex items-center gap-2"
                        >
                            <Plus className="w-5 h-5" />
                            Create Project
                        </button>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredProjects.map((project: any) => (
                        <div key={project.id} className="card hover:border-dark-600 transition-colors">
                            <div className="flex items-start justify-between mb-4">
                                <div className="w-12 h-12 bg-primary-600/20 rounded-xl flex items-center justify-center">
                                    <FolderKanban className="w-6 h-6 text-primary-400" />
                                </div>
                                <div className="relative group">
                                    <button className="p-2 hover:bg-dark-700 rounded-lg">
                                        <MoreVertical className="w-5 h-5 text-dark-400" />
                                    </button>
                                    <div className="absolute right-0 top-full mt-1 w-48 bg-dark-800 border border-dark-700 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                                        <Link
                                            to={`/projects/${project.id}`}
                                            className="flex items-center gap-2 px-4 py-2 hover:bg-dark-700 text-sm"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                            Open Project
                                        </Link>
                                        <button
                                            onClick={() => deleteMutation.mutate(project.id)}
                                            className="flex items-center gap-2 px-4 py-2 hover:bg-dark-700 text-sm text-red-400 w-full"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <Link to={`/projects/${project.id}`}>
                                <h3 className="font-semibold text-lg mb-1 hover:text-primary-400">
                                    {project.name}
                                </h3>
                            </Link>

                            {project.websiteDomain && (
                                <div className="flex items-center gap-2 text-dark-400 text-sm mb-4">
                                    <Globe className="w-4 h-4" />
                                    {project.websiteDomain}
                                </div>
                            )}

                            <div className="flex items-center justify-between pt-4 border-t border-dark-700">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${project.status === 'active'
                                        ? 'bg-green-500/20 text-green-400'
                                        : 'bg-dark-600 text-dark-400'
                                    }`}>
                                    {project.status}
                                </span>
                                <span className="text-dark-500 text-sm">
                                    {project.industry}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Create Modal */}
            {showCreateModal && (
                <CreateProjectModal onClose={() => setShowCreateModal(false)} />
            )}
        </div>
    );
}

function CreateProjectModal({ onClose }: { onClose: () => void }) {
    const [name, setName] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [industry, setIndustry] = useState('other');
    const queryClient = useQueryClient();

    const createMutation = useMutation({
        mutationFn: (data: any) => projectsApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['projects'] });
            onClose();
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        createMutation.mutate({ name, websiteUrl, industry });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-dark-800 rounded-xl border border-dark-700 p-6 w-full max-w-md">
                <h2 className="text-xl font-semibold mb-6">Create New Project</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="label">Project Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="input"
                            placeholder="My Awesome Project"
                            required
                        />
                    </div>

                    <div>
                        <label className="label">Website URL (optional)</label>
                        <input
                            type="url"
                            value={websiteUrl}
                            onChange={(e) => setWebsiteUrl(e.target.value)}
                            className="input"
                            placeholder="https://example.com"
                        />
                    </div>

                    <div>
                        <label className="label">Industry</label>
                        <select
                            value={industry}
                            onChange={(e) => setIndustry(e.target.value)}
                            className="input"
                        >
                            <option value="ecommerce">E-commerce</option>
                            <option value="saas">SaaS</option>
                            <option value="agency">Agency</option>
                            <option value="healthcare">Healthcare</option>
                            <option value="finance">Finance</option>
                            <option value="education">Education</option>
                            <option value="other">Other</option>
                        </select>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-secondary flex-1"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={createMutation.isPending}
                            className="btn btn-primary flex-1"
                        >
                            {createMutation.isPending ? 'Creating...' : 'Create Project'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
